<template>
  <b-card
    no-body
    class="p-1"
  >
    <!-- Зона ПРОЦЕССА: выбор / создание / версия / публикация -->
    <div class="d-flex flex-wrap align-items-center mb-1 pb-1 border-bottom">
      <span class="text-muted mr-50 small">Процесс:</span>
      <b-form-select
        v-model="selectedProcessId"
        size="sm"
        style="width: 240px"
        class="mr-1"
        :options="processOptions"
        @change="loadSchema"
      />
      <b-button
        size="sm"
        variant="outline-primary"
        class="mr-1"
        @click="newProcess"
      >
        <feather-icon icon="PlusIcon" /> Новый процесс
      </b-button>
      <b-badge
        v-if="process"
        :variant="process.status_code === 'published' ? 'success' : 'secondary'"
        class="mr-1"
      >
        {{ process.status_code }} · v{{ process.version }}
      </b-badge>
      <div class="flex-grow-1" />
      <b-button
        v-if="process"
        size="sm"
        :variant="dirty ? 'warning' : 'outline-success'"
        class="mr-1"
        :disabled="!dirty || saving"
        @click="saveAll"
      >
        <feather-icon icon="SaveIcon" />
        {{ saving ? 'Сохраняю…' : (dirty ? 'Сохранить изменения' : 'Сохранено') }}
      </b-button>
      <b-button
        v-if="process"
        size="sm"
        variant="outline-secondary"
        class="mr-1"
        @click="showJson = !showJson"
      >
        <feather-icon icon="CodeIcon" /> {{ showJson ? 'Холст' : 'JSON' }}
      </b-button>
      <b-button
        v-if="process"
        size="sm"
        variant="primary"
        :disabled="!entryStageId"
        @click="publish"
      >
        <feather-icon icon="UploadCloudIcon" /> Опубликовать
      </b-button>
      <!-- Запуск живого экземпляра: только у опубликованного процесса -->
      <b-button
        v-if="process && process.status_code === 'published'"
        size="sm"
        variant="success"
        class="ml-1"
        :disabled="!entryStageId"
        :title="entryStageId ? 'Запустить новый экземпляр процесса' : 'Сначала задайте стартовый этап'"
        @click="openLaunch"
      >
        <feather-icon icon="PlayIcon" /> Запустить
        <small class="ml-25">тест</small>
      </b-button>
    </div>

    <!-- Право на запуск: кто из пользователей может запускать этот процесс -->
    <div
      v-if="process"
      class="d-flex flex-wrap align-items-center mb-1 small"
    >
      <span class="text-muted mr-50">Кто может запускать:</span>
      <b-form-input
        v-model="startableBy"
        size="sm"
        placeholder="пусто = все, напр. role('user')"
        style="max-width: 260px"
        class="mr-50"
      />
      <b-form-select
        v-model="startablePick"
        size="sm"
        style="max-width: 150px"
        class="mr-50"
        :options="[{ value: null, text: '+ роль…' }].concat(assigneeRoles.map(r => ({ value: r.sid, text: r.sid })))"
        @change="onPickStartableRole"
      />
      <b-button
        size="sm"
        variant="outline-primary"
        class="py-25"
        @click="saveStartable"
      >
        Сохранить право
      </b-button>
    </div>

    <div
      v-if="!process"
      class="text-muted p-2"
    >
      Выберите процесс или создайте новый.
    </div>

    <!-- JSON-режим -->
    <b-form-textarea
      v-else-if="showJson"
      :value="schemaJson"
      rows="20"
      readonly
      class="font-small-3"
      style="font-family: monospace"
    />

    <!-- Список этапов + Холст + панель свойств -->
    <div
      v-else
      class="d-flex"
    >
      <!-- ЭТАПЫ: список всех этапов процесса (видно даже если узлы наложились) -->
      <div class="bpm-stagelist border rounded mr-1 p-50">
        <div class="d-flex justify-content-between align-items-center mb-50">
          <small class="font-weight-bold">Этапы ({{ nodes.length }})</small>
          <b-button
            size="sm"
            variant="flat-primary"
            class="p-25"
            title="Добавить этап"
            @click="addStage"
          >
            <feather-icon icon="PlusIcon" /> Этап
          </b-button>
        </div>
        <div
          v-if="!nodes.length"
          class="text-muted small"
        >
          Пока нет этапов. Нажмите «+ Этап».
        </div>
        <div
          v-for="n in nodes"
          :key="`li${n.id}`"
          class="bpm-stage-item rounded p-50 mb-25 cursor-pointer"
          :class="{ 'bpm-stage-item-active': n.id === selectedStageId }"
          @click="selectStage(n.id)"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              v-if="n.id === entryStageId"
              icon="PlayIcon"
              size="12"
              class="text-success mr-25 flex-shrink-0"
            />
            <span class="font-weight-bold text-truncate">{{ n.label || 'без названия' }}</span>
          </div>
          <small class="text-muted text-truncate d-block">{{ n.assignees_expr || 'без исполнителей' }}</small>
        </div>
      </div>

      <div
        ref="canvas"
        class="bpm-canvas flex-grow-1 mr-1"
        @mousemove="onDrag"
        @mouseup="endDrag"
        @mouseleave="endDrag"
        @click.self="selectedStageId = null"
      >
        <svg class="bpm-edges">
          <defs>
            <marker
              id="bpm-arrow"
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
            >
              <path
                d="M0,0 L8,4 L0,8 Z"
                fill="#9aa0ac"
              />
            </marker>
          </defs>
          <g
            v-for="e in edges"
            :key="`e${e.id}`"
          >
            <line
              :x1="e.x1"
              :y1="e.y1"
              :x2="e.x2"
              :y2="e.y2"
              stroke="#9aa0ac"
              stroke-width="1.5"
              marker-end="url(#bpm-arrow)"
            />
            <text
              :x="(e.x1 + e.x2) / 2"
              :y="(e.y1 + e.y2) / 2 - 4"
              fill="#6e6b7b"
              font-size="11"
              text-anchor="middle"
            >{{ e.label }}</text>
          </g>
        </svg>

        <div
          v-for="n in nodes"
          :key="`n${n.id}`"
          class="bpm-stage rounded shadow-sm"
          :class="{
            'border-primary': n.id === selectedStageId,
            'bpm-entry': n.id === entryStageId,
          }"
          :style="{ left: n.x + 'px', top: n.y + 'px' }"
          @mousedown="startDrag(n, $event)"
          @click.stop="selectedStageId = n.id"
        >
          <div class="font-weight-bold text-truncate">
            <feather-icon
              v-if="n.id === entryStageId"
              icon="PlayIcon"
              size="12"
              class="text-success"
            />
            {{ n.label }}
          </div>
          <small class="text-muted text-truncate d-block">{{ n.assignees_expr || 'без исполнителей' }}</small>
        </div>
      </div>

      <!-- Панель свойств -->
      <div
        v-if="selectedStage"
        class="bpm-props border rounded p-1"
      >
        <h6 class="d-flex justify-content-between">
          Этап #{{ selectedStage.id }}
          <b-button
            size="sm"
            variant="flat-danger"
            class="p-25"
            @click="removeStage(selectedStage)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </h6>
        <!-- действия над этапом -->
        <div class="d-flex mb-1">
          <b-button
            size="sm"
            variant="outline-success"
            :disabled="selectedStage.id === entryStageId"
            @click="entryStageId = selectedStage.id"
          >
            {{ selectedStage.id === entryStageId ? '★ Стартовый' : 'Сделать стартовым' }}
          </b-button>
          <small
            v-if="dirty"
            class="text-warning ml-1 align-self-center"
          >● есть несохранённые изменения — кнопка «Сохранить» вверху</small>
        </div>

        <app-collapse type="margin">
          <!-- 1. Основное -->
          <app-collapse-item
            title="Основное"
            :is-visible="true"
          >
            <b-form-group
              label="Название этапа"
              class="mb-50"
            >
              <b-form-input
                v-model="selectedStage.label"
                size="sm"
              />
            </b-form-group>
            <b-form-group
              label="Описание"
              class="mb-0"
            >
              <b-form-input
                v-model="selectedStage.descr"
                size="sm"
              />
            </b-form-group>
          </app-collapse-item>

          <!-- 2. Кто выполняет -->
          <app-collapse-item
            :title="`Кто выполняет · ${executorKindLabel(selectedStage.executor_kind)}`"
            :is-visible="true"
          >
            <b-form-group
              label="Тип исполнителя"
              class="mb-50"
            >
              <b-form-select
                v-model="selectedStage.executor_kind"
                size="sm"
                :options="[
                  { value: 'HUMAN', text: 'Человек (по исполнителям)' },
                  { value: 'AI', text: 'ИИ (решает по промпту)' },
                  { value: 'AUTO', text: 'Авто (без исполнителя)' },
                  { value: 'SUBPROCESS', text: 'Подпроцесс (call-activity)' },
                ]"
              />
            </b-form-group>

            <!-- AI: промпт для ИИ -->
            <template v-if="selectedStage.executor_kind === 'AI'">
              <b-form-group
                label="Промпт для ИИ (что проверить / как решить)"
                class="mb-50"
              >
                <b-form-textarea
                  v-model="selectedStage.instructions"
                  rows="4"
                  size="sm"
                  placeholder="Напр.: Проверь текст рассылки на спам, КАПС, оскорбления. Если ок — выбери «корректен», иначе «есть проблемы»."
                />
              </b-form-group>
              <small class="text-muted d-block mb-50">
                ИИ читает входные данные этапа + этот промпт + <b>описания исходов</b> (вкладка «Исходы» → поле «Описание для ИИ»)
                и сам выбирает исход. Причину пишет в данные («ии_замечание»).
              </small>
            </template>

            <!-- HUMAN: конструктор исполнителей -->
            <template v-if="(selectedStage.executor_kind || 'HUMAN') === 'HUMAN'">
              <small class="text-muted d-block mb-50">
                Кто получит этот этап как задачу. Соберите из кнопок и списков ниже — выражение и предпросмотр обновятся сами.
              </small>
              <div class="mb-50">
                <b-button size="sm" variant="outline-primary" class="mr-25 mb-25 py-25" @click="addAssignee('$initiator')">
                  + Инициатор
                </b-button>
                <b-button size="sm" variant="outline-primary" class="mr-25 mb-25 py-25" @click="addAssignee('$currentUser')">
                  + Текущий пользователь
                </b-button>
              </div>
              <div class="d-flex mb-50">
                <b-form-select
                  v-model="rolePick"
                  size="sm"
                  class="mr-25"
                  :options="[{ value: null, text: '+ по роли…' }].concat(assigneeRoles.map(r => ({ value: r.sid, text: `${r.name} (${r.sid})` })))"
                  @change="onPickRole"
                />
                <b-form-select
                  v-model="userPick"
                  size="sm"
                  :options="[{ value: null, text: '+ пользователя…' }].concat(assigneeUsers.map(u => ({ value: u.sid, text: u.name })))"
                  @change="onPickUser"
                />
              </div>
              <b-form-input
                v-model="selectedStage.assignees_expr"
                size="sm"
                placeholder="$initiator, role('CFO')"
                @input="debouncedPreview"
              />
              <div class="mt-50 small">
                <div v-if="assigneePreview.users && assigneePreview.users.length" class="text-success">
                  → Уйдёт: {{ assigneePreview.users.join(', ') }}
                </div>
                <div
                  v-for="(n, ni) in (assigneePreview.notes || [])"
                  :key="`an${ni}`"
                  class="text-muted"
                >
                  {{ n }}
                </div>
                <b-button
                  v-if="selectedStage.assignees_expr"
                  size="sm"
                  variant="flat-secondary"
                  class="p-0 mt-25"
                  @click="clearAssignees"
                >
                  очистить
                </b-button>
              </div>
            </template>

            <!-- SUBPROCESS: дочерний процесс + маппинги -->
            <template v-else-if="selectedStage.executor_kind === 'SUBPROCESS'">
              <b-form-group
                label="Дочерний процесс"
                class="mb-50"
              >
                <b-form-select
                  v-model.number="selectedStage.subprocess_def_id"
                  size="sm"
                  :options="subprocessOptions"
                />
              </b-form-group>
              <b-form-group
                label="Маппинг входа {слот_родителя: слот_ребёнка} — пусто = как есть"
                class="mb-50"
              >
                <b-form-textarea
                  v-model="selectedStage.in_slot_map_json"
                  rows="2"
                  size="sm"
                  placeholder='{"сумма": "amount"}'
                />
              </b-form-group>
              <b-form-group
                label="Маппинг выхода {слот_ребёнка: выход_родителя} — пусто = как есть"
                class="mb-0"
              >
                <b-form-textarea
                  v-model="selectedStage.out_slot_map_json"
                  rows="2"
                  size="sm"
                  placeholder='{"result": "итог"}'
                />
              </b-form-group>
            </template>

            <!-- AUTO -->
            <div
              v-else
              class="text-muted small"
            >
              Авто-этап: выполняется без исполнителя — сразу выбирается исход (по условию).
            </div>
          </app-collapse-item>

          <!-- 3. Действие этапа (авто-запись в таблицу Кубуса) -->
          <app-collapse-item
            :title="`Действие${selectedStage.action_kind && selectedStage.action_kind !== 'NONE' ? ' · запись в таблицу' : ''}`"
          >
            <small class="text-muted d-block mb-50">
              Что этап делает автоматически (помимо выбора исхода). Например — сохранить данные в таблицу Кубуса.
            </small>
            <b-form-group
              label="Действие"
              class="mb-50"
            >
              <b-form-select
                v-model="selectedStage.action_kind"
                size="sm"
                :options="[
                  { value: 'NONE', text: 'Нет' },
                  { value: 'SAVE_RECORD', text: 'Сохранить запись в таблицу' },
                ]"
              />
            </b-form-group>

            <!-- Сохранить запись в таблицу -->
            <template v-if="selectedStage.action_kind === 'SAVE_RECORD'">
              <b-form-group
                label="Таблица (объект Кубуса)"
                class="mb-50"
              >
                <b-form-select
                  v-model.number="selectedStage.action_object_id"
                  size="sm"
                  :options="objectOptions"
                />
              </b-form-group>
              <label class="small text-muted mb-0">Колонка ← чем заполнить</label>
              <div
                v-for="(m, mi) in (selectedStage.actionMap || [])"
                :key="mi"
                class="d-flex align-items-center mb-25"
              >
                <b-form-input
                  v-model="m.to"
                  size="sm"
                  class="mr-25"
                  style="width: 90px"
                  placeholder="колонка"
                />
                <span class="mr-25 text-muted">=</span>
                <b-form-select
                  v-model="m.src"
                  size="sm"
                  class="mr-25"
                  style="width: 110px"
                  :options="[
                    { value: 'slot', text: 'поле этапа' },
                    { value: 'now', text: 'тек. дата' },
                    { value: 'const', text: 'значение' },
                  ]"
                />
                <b-form-select
                  v-if="m.src === 'slot'"
                  v-model="m.value"
                  size="sm"
                  class="mr-25"
                  :options="outputOptions(selectedStage.id)"
                />
                <b-form-input
                  v-else-if="m.src === 'const'"
                  v-model="m.value"
                  size="sm"
                  class="mr-25"
                  placeholder="значение"
                />
                <b-button
                  size="sm"
                  variant="flat-danger"
                  class="p-25"
                  @click="selectedStage.actionMap.splice(mi, 1)"
                >
                  <feather-icon icon="XIcon" />
                </b-button>
              </div>
              <b-button
                size="sm"
                variant="flat-primary"
                class="p-25"
                @click="addActionMap"
              >
                <feather-icon icon="PlusIcon" /> добавить колонку
              </b-button>
            </template>

          </app-collapse-item>

          <!-- 4. Срок, инструкция, форма -->
          <app-collapse-item title="Срок · инструкция · форма">
            <b-form-group
              label="Срок выполнения, мин (0 — без срока)"
              class="mb-50"
            >
              <b-form-input
                v-model.number="selectedStage.deadline_minutes"
                type="number"
                min="0"
                size="sm"
                placeholder="напр. 1440 = 1 день"
              />
            </b-form-group>
            <b-form-group
              label="Инструкция исполнителю"
              class="mb-50"
            >
              <b-form-textarea
                v-model="selectedStage.instructions"
                rows="3"
                size="sm"
                placeholder="Что нужно сделать на этом этапе"
              />
            </b-form-group>
            <b-form-group
              label="ID объекта-формы ввода (необязательно)"
              class="mb-0"
            >
              <b-form-input
                v-model.number="selectedStage.form_obj_id"
                type="number"
                min="0"
                size="sm"
                placeholder="объект, который откроет исполнитель"
              />
            </b-form-group>
          </app-collapse-item>

          <!-- 4. Входные данные -->
          <app-collapse-item :title="`Входные данные (${stageInparams(selectedStage.id).length})`">
            <small class="text-muted d-block mb-50">
              Данные прошлого этапа <b>переходят сюда автоматически</b> (видны в задаче как «Исходные данные»).
              Поля здесь нужны, только если этап должен <b>дождаться</b> конкретных данных: обязательное (★) поле
              не даст этапу стартовать, пока такое значение не придёт.
            </small>
            <div
              v-for="p in stageInparams(selectedStage.id)"
              :key="p.id"
              class="border rounded p-50 mb-50"
            >
              <div class="d-flex mb-25">
                <b-form-input
                  v-model="p.param_name"
                  size="sm"
                  class="mr-25"
                  placeholder="имя поля"
                />
                <b-form-select
                  v-model="p.datatype"
                  size="sm"
                  style="width: 150px"
                  :options="datatypes"
                />
              </div>
              <b-form-select
                v-if="p.datatype === 'ref'"
                v-model.number="p.ref_object_id"
                size="sm"
                class="mb-25"
                :options="objectOptions"
              />
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="p.required"
                  class="mr-50"
                >
                  обяз.
                </b-form-checkbox>
                <b-button
                  size="sm"
                  variant="flat-danger"
                  class="p-25"
                  @click="deleteInparam(p)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </div>
            <b-button
              size="sm"
              variant="outline-primary"
              class="py-25"
              @click="addInparam(selectedStage)"
            >
              <feather-icon icon="PlusIcon" /> Добавить вход
            </b-button>
          </app-collapse-item>

          <!-- 5. Выходные данные -->
          <app-collapse-item :title="`Выходные данные (${stageOutparams(selectedStage.id).length})`">
            <small class="text-muted d-block mb-50">
              Данные, которые исполнитель должен заполнить, чтобы завершить этап (обязательные — со звёздочкой).
            </small>
            <div
              v-for="op in stageOutparams(selectedStage.id)"
              :key="op.id"
              class="border rounded p-50 mb-50"
            >
              <div class="d-flex mb-25">
                <b-form-input
                  v-model="op.param_name"
                  size="sm"
                  class="mr-25"
                  placeholder="имя поля"
                />
                <b-form-select
                  v-model="op.datatype"
                  size="sm"
                  style="width: 150px"
                  :options="datatypes"
                />
              </div>
              <b-form-select
                v-if="op.datatype === 'ref'"
                v-model.number="op.ref_object_id"
                size="sm"
                class="mb-25"
                :options="objectOptions"
              />
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="op.required"
                  class="mr-50"
                >
                  обяз.
                </b-form-checkbox>
                <b-button
                  size="sm"
                  variant="flat-danger"
                  class="p-25"
                  @click="deleteOutparam(op)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </div>
            <b-button
              size="sm"
              variant="outline-primary"
              class="py-25"
              @click="addOutparam(selectedStage)"
            >
              <feather-icon icon="PlusIcon" /> Добавить выход
            </b-button>
          </app-collapse-item>

          <!-- 5. Исходы -->
          <app-collapse-item
            :title="`Исходы (${stageOutcomes(selectedStage.id).length})`"
            :is-visible="true"
          >
            <small class="text-muted d-block mb-50">
              Кнопки, которыми исполнитель завершает этап. Поле «Дальше» пустое = завершить процесс.
            </small>
            <div
              v-if="!stageOutcomes(selectedStage.id).length"
              class="text-warning small mb-50"
            >
              Нет исходов — с этапа некуда выйти. Добавьте хотя бы один.
            </div>
            <div
              v-for="o in stageOutcomes(selectedStage.id)"
              :key="`o${o.id}`"
              class="border rounded p-50 mb-50"
            >
              <label class="small text-muted mb-0">Кнопка / название исхода</label>
              <b-form-input
                v-model="o.label"
                size="sm"
                class="mb-50"
                placeholder="напр. Утвердить / Отклонить / Отправить"
              />
              <label class="small text-muted mb-0">Описание для ИИ — когда и почему выбирать этот исход</label>
              <b-form-input
                v-model="o.descr"
                size="sm"
                class="mb-50"
                placeholder="напр. текст без спама и КАПСа — можно публиковать"
              />
              <div class="d-flex mb-50">
                <div class="mr-25 flex-grow-1">
                  <label class="small text-muted mb-0">Тип</label>
                  <b-form-select
                    v-model="o.outcome_type"
                    size="sm"
                    :options="[
                      { value: 'manual', text: 'кнопка (человек)' },
                      { value: 'auto', text: 'авто-переход' },
                    ]"
                  />
                </div>
                <div class="flex-grow-1">
                  <label class="small text-muted mb-0">Дальше →</label>
                  <b-form-select
                    v-model="o.next_stage_id"
                    size="sm"
                    :options="nextStageOptions(selectedStage.id)"
                  />
                </div>
              </div>

              <!-- Передача данных в следующий этап (настройка в исходе) -->
              <div
                v-if="o.next_stage_id"
                class="mb-50 pl-50 border-left"
              >
                <label class="small text-muted mb-0">Передать данные в следующий этап</label>
                <div
                  v-for="(m, mi) in (o.slotMap || [])"
                  :key="mi"
                  class="d-flex align-items-center mb-25"
                >
                  <b-form-select
                    v-model="m.from"
                    size="sm"
                    class="mr-25"
                    :options="outputOptions(selectedStage.id)"
                  />
                  <feather-icon icon="ArrowRightIcon" class="mr-25 text-muted" />
                  <b-form-input
                    v-model="m.to"
                    size="sm"
                    class="mr-25"
                    placeholder="имя во входе"
                  />
                  <b-button
                    size="sm"
                    variant="flat-danger"
                    class="p-25"
                    @click="o.slotMap.splice(mi, 1)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </div>
                <b-button
                  size="sm"
                  variant="flat-primary"
                  class="p-25"
                  @click="addMap(o)"
                >
                  <feather-icon icon="PlusIcon" /> передать поле
                </b-button>
                <small class="d-block text-muted">
                  Пусто = все данные этапа перейдут автоматически. Здесь можно задать точно: какой выход → в какое поле след. этапа.
                </small>
              </div>

              <div class="d-flex">
                <b-button
                  size="sm"
                  variant="flat-danger"
                  class="py-25"
                  @click="removeOutcome(o)"
                >
                  <feather-icon icon="Trash2Icon" /> Удалить исход
                </b-button>
              </div>
            </div>
            <b-button
              size="sm"
              variant="outline-primary"
              class="py-25"
              @click="addOutcome(selectedStage)"
            >
              <feather-icon icon="PlusIcon" /> Добавить исход
            </b-button>
          </app-collapse-item>
        </app-collapse>
      </div>
    </div>

    <!-- Подтверждение запуска: показывает последствие (инициатор + кому уйдёт 1-я задача) -->
    <b-modal
      v-model="launch.show"
      title="Запустить процесс?"
      centered
      hide-footer
    >
      <template v-if="!launch.startedId">
        <p class="mb-50">
          Процесс: <b>{{ process && process.name }}</b> <span class="text-muted">(v{{ process && process.version }})</span>
        </p>
        <p class="mb-50">
          Вы станете <b>инициатором</b>: {{ launch.initiatorName }}
        </p>
        <p class="mb-25">
          Первый этап «<b>{{ launch.entryLabel }}</b>» получит задачу:
        </p>
        <ul class="mb-50 pl-2">
          <li
            v-for="(u, ui) in (launch.preview.users || [])"
            :key="`lu${ui}`"
          >
            {{ u }}
          </li>
          <li
            v-for="(n, ni) in (launch.preview.notes || [])"
            :key="`ln${ni}`"
            class="text-muted"
          >
            {{ n }}
          </li>
          <li
            v-if="!(launch.preview.users || []).length && !(launch.preview.notes || []).length"
            class="text-warning"
          >
            исполнители не заданы — задача не назначится
          </li>
        </ul>
        <p class="small text-warning mb-1">
          ⚠ Этим людям придёт задача в «Мои задачи». Это реальный прогон.
        </p>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="launch.show = false"
          >
            Отмена
          </b-button>
          <b-button
            variant="success"
            :disabled="launch.loading"
            @click="confirmLaunch"
          >
            <feather-icon icon="PlayIcon" /> {{ launch.loading ? 'Запускаю…' : 'Запустить' }}
          </b-button>
        </div>
      </template>
      <template v-else>
        <p class="mb-1">
          <feather-icon
            icon="CheckCircleIcon"
            class="text-success"
          /> Экземпляр <b>#{{ launch.startedId }}</b> запущен.
        </p>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-primary"
            class="mr-1"
            @click="goAfterLaunch('cubus-bpm-monitor')"
          >
            Открыть в мониторинге
          </b-button>
          <b-button
            variant="primary"
            @click="goAfterLaunch('cubus-bpm-inbox')"
          >
            Мои задачи
          </b-button>
        </div>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BButton, BBadge, BFormSelect, BFormInput, BFormGroup, BFormTextarea, BFormCheckbox, BModal,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

const NODE_W = 150
const NODE_H = 54

export default {
  name: 'CbsBpmDesigner',
  components: {
    BCard, BButton, BBadge, BFormSelect, BFormInput, BFormGroup, BFormTextarea, BFormCheckbox, BModal,
    AppCollapse, AppCollapseItem,
  },
  data() {
    return {
      processes: [],
      selectedProcessId: null,
      process: null,
      dirty: false,
      saving: false,
      loadingSchema: false,
      nodes: [],
      outcomes: [],
      outparams: [],
      inparams: [],
      objects: [],
      datatypes: [
        { value: 'string', text: 'Строка' },
        { value: 'text', text: 'Текст' },
        { value: 'number', text: 'Число' },
        { value: 'date', text: 'Дата' },
        { value: 'boolean', text: 'Да / Нет' },
        { value: 'file', text: 'Файл' },
        { value: 'ref', text: 'Ссылка на объект' },
      ],
      selectedStageId: null,
      entryStageId: null,
      showJson: false,
      drag: null,
      // конструктор исполнителей
      assigneeRoles: [],
      assigneeUsers: [],
      assigneePreview: { users: [], notes: [] },
      rolePick: null,
      userPick: null,
      // право на запуск процесса
      startableBy: '',
      startablePick: null,
      // запуск экземпляра
      launch: {
        show: false,
        loading: false,
        startedId: null,
        entryLabel: '',
        initiatorName: 'вы',
        preview: { users: [], notes: [] },
      },
    }
  },
  computed: {
    processOptions() {
      return [{ value: null, text: '— выберите процесс —' }]
        .concat(this.processes.map(p => ({ value: p.id, text: `#${p.id} ${p.name} (${p.status_code})` })))
    },
    selectedStage() {
      return this.nodes.find(n => n.id === this.selectedStageId) || null
    },
    edges() {
      const byId = {}
      this.nodes.forEach(n => { byId[n.id] = n })
      return this.outcomes
        .filter(o => o.next_stage_id && byId[o.stage_id] && byId[o.next_stage_id])
        .map(o => {
          const a = byId[o.stage_id]; const b = byId[o.next_stage_id]
          return {
            id: o.id,
            label: o.label,
            x1: a.x + NODE_W,
            y1: a.y + NODE_H / 2,
            x2: b.x,
            y2: b.y + NODE_H / 2,
          }
        })
    },
    objectOptions() {
      return [{ value: null, text: '— выберите объект —' }].concat(
        this.objects.map(o => ({ value: o.id, text: `${o.name} (${o.sid})` })),
      )
    },
    subprocessOptions() {
      // published processes other than the one being edited (avoid trivial self-recursion)
      return [{ value: null, text: '— выберите процесс —' }].concat(
        this.processes
          .filter(p => p.id !== this.selectedProcessId && (p.status_code === 'published' || p.status === 'published'))
          .map(p => ({ value: p.id, text: p.name })),
      )
    },
    schemaJson() {
      return JSON.stringify({
        process: this.process,
        stages: this.nodes.map(n => ({
          id: n.id, label: n.label, assignees_expr: n.assignees_expr, pos_x: n.x, pos_y: n.y, descr: n.descr, deadline_minutes: n.deadline_minutes, instructions: n.instructions, form_obj_id: n.form_obj_id,
          executor_kind: n.executor_kind, subprocess_def_id: n.subprocess_def_id, in_slot_map_json: n.in_slot_map_json, out_slot_map_json: n.out_slot_map_json,
        })),
        outcomes: this.outcomes,
      }, null, 2)
    },
  },
  watch: {
    selectedStageId() {
      this.refreshAssigneePreview()
    },
    // глобальный dirty-трекинг: любое изменение этапов/исходов/слотов/прав → «есть несохранённые изменения»
    nodes: { deep: true, handler() { this.markDirty() } },
    outcomes: { deep: true, handler() { this.markDirty() } },
    inparams: { deep: true, handler() { this.markDirty() } },
    outparams: { deep: true, handler() { this.markDirty() } },
    startableBy() { this.markDirty() },
  },
  created() {
    useCubus.setPageTitle('Дизайнер процессов')
    this.loadProcesses()
    this.api('bpm_objects', {}).then(r => { if (r) this.objects = r.objects || [] })
  },
  methods: {
    // ---- конструктор исполнителей ----
    refreshAssigneePreview() {
      const expr = this.selectedStage ? (this.selectedStage.assignees_expr || '') : ''
      this.api('bpm_assignees_preview', { expr }).then(r => {
        if (!r) return
        this.assigneeRoles = r.roles || []
        this.assigneeUsers = r.users || []
        this.assigneePreview = r.preview || { users: [], notes: [] }
      })
    },
    debouncedPreview() {
      clearTimeout(this._previewT)
      this._previewT = setTimeout(() => this.refreshAssigneePreview(), 400)
    },
    addAssignee(token) {
      if (!this.selectedStage) return
      const cur = (this.selectedStage.assignees_expr || '').trim()
      // не дублируем один и тот же токен
      const parts = cur ? cur.split(/\s*(?:,|\bor\b)\s*/i).map(s => s.trim()).filter(Boolean) : []
      if (!parts.includes(token)) parts.push(token)
      this.$set(this.selectedStage, 'assignees_expr', parts.join(', '))
      this.refreshAssigneePreview()
    },
    onPickRole(sid) {
      if (sid) this.addAssignee(`role('${sid}')`)
      this.rolePick = null
    },
    onPickUser(sid) {
      if (sid) this.addAssignee(sid)
      this.userPick = null
    },
    clearAssignees() {
      if (!this.selectedStage) return
      this.$set(this.selectedStage, 'assignees_expr', '')
      this.refreshAssigneePreview()
    },
    api(method, param) {
      return useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method, param, mode: 'sync' },
      }).then(r => {
        if (r.data.error) { useCubus.toastError(this, r.data.error); return null }
        return r.data.result
      })
    },
    loadProcesses() {
      this.api('bpm_processes', {}).then(r => { if (r) this.processes = r.processes || [] })
    },
    markDirty() {
      if (!this.loadingSchema) this.dirty = true
    },
    // ГЛОБАЛЬНОЕ сохранение: один клик сохраняет все правки этапов/исходов/слотов/прав запуска
    saveAll() {
      if (!this.dirty || this.saving) return
      this.saving = true
      const calls = []
      this.nodes.forEach(n => calls.push(this.api('bpm_save_stage', {
        id: n.id, label: n.label, assignees_expr: n.assignees_expr, descr: n.descr, pos_x: Math.round(n.x), pos_y: Math.round(n.y),
        deadline_minutes: Number(n.deadline_minutes) || 0, instructions: n.instructions || '', form_obj_id: Number(n.form_obj_id) || 0,
        executor_kind: n.executor_kind || 'HUMAN', subprocess_def_id: Number(n.subprocess_def_id) || 0,
        in_slot_map_json: n.in_slot_map_json || '', out_slot_map_json: n.out_slot_map_json || '',
        action_kind: n.action_kind || 'NONE', action_object_id: Number(n.action_object_id) || 0, action_map_json: JSON.stringify((n.actionMap || []).filter(m => m.to).map(m => ({ to: m.to, src: m.src || 'slot', value: m.src === 'now' ? '' : (m.value || '') }))),
      })))
      this.outcomes.forEach(o => calls.push(this.api('bpm_save_outcome', {
        id: o.id, label: o.label, outcome_type: o.outcome_type, next_stage_id: o.next_stage_id || null,
        slot_map_json: JSON.stringify((o.slotMap || []).filter(m => m.from && m.to)), descr: o.descr || '',
      })))
      this.inparams.forEach(p => calls.push(this.api('bpm_save_input', {
        id: p.id, param_name: p.param_name, required: !!p.required, datatype: p.datatype || 'string', ref_object_id: p.datatype === 'ref' ? (p.ref_object_id || 0) : 0,
      })))
      this.outparams.forEach(op => calls.push(this.api('bpm_save_output', {
        id: op.id, param_name: op.param_name, required: !!op.required, datatype: op.datatype || 'string', ref_object_id: op.datatype === 'ref' ? (op.ref_object_id || 0) : 0,
      })))
      calls.push(this.api('bpm_set_startable', { process_def_id: this.selectedProcessId, expr: this.startableBy || '' }))
      return Promise.all(calls)
        .then(() => { this.saving = false; this.dirty = false; useCubus.toastMessage(this, 'Изменения сохранены'); return true })
        .catch(() => { this.saving = false; useCubus.toastError(this, 'Не удалось сохранить часть изменений'); return false })
    },
    loadSchema() {
      if (!this.selectedProcessId) { this.process = null; return }
      this.loadingSchema = true
      this.api('bpm_schema', { process_def_id: this.selectedProcessId }).then(r => {
        if (!r) { this.loadingSchema = false; return }
        this.process = r.process || null
        this.entryStageId = (r.process && r.process.entry_stage_id) || null
        this.startableBy = (r.process && r.process.startable_by) || ''
        if (!this.assigneeRoles.length) {
          this.api('bpm_assignees_preview', { expr: '' }).then(rr => { if (rr) this.assigneeRoles = rr.roles || [] })
        }
        this.outcomes = (r.outcomes || []).map(o => ({ ...o, next_stage_id: o.next_stage_id || null, descr: o.descr || "", slotMap: this.parseMap(o.slot_map_json) }))
        this.outparams = (r.outparams || []).map(op => ({ ...op, required: op.required === true || op.required === 1 || op.required === '1' || op.required === 't', datatype: op.datatype || 'string', ref_object_id: op.ref_object_id ? Number(op.ref_object_id) : null }))
        this.inparams = (r.params || []).map(p => ({ ...p, required: p.required === true || p.required === 1 || p.required === '1' || p.required === 't', datatype: p.datatype || 'string', ref_object_id: p.ref_object_id ? Number(p.ref_object_id) : null }))
        this.nodes = (r.stages || []).map((s, i) => ({
          id: s.id,
          label: s.label,
          assignees_expr: s.assignees_expr || '',
          descr: s.descr || '',
          deadline_minutes: Number(s.deadline_minutes) || 0,
          instructions: s.instructions || '',
          form_obj_id: Number(s.form_obj_id) || 0,
          executor_kind: s.executor_kind || 'HUMAN',
          subprocess_def_id: Number(s.subprocess_def_id) || null,
          in_slot_map_json: s.in_slot_map_json || '',
          out_slot_map_json: s.out_slot_map_json || '',
          action_kind: s.action_kind || 'NONE',
          action_object_id: Number(s.action_object_id) || null,
          actionMap: this.parseActionMap(s.action_map_json),
          x: Number(s.pos_x) || (40 + (i % 4) * 180),
          y: Number(s.pos_y) || (40 + Math.floor(i / 4) * 110),
        }))
        // сохраняем выбранный этап, если он ещё существует — иначе панель работы
        // над этапом закрывалась при каждой перезагрузке схемы (публикация и т.п.)
        if (!this.nodes.some(n => n.id === this.selectedStageId)) this.selectedStageId = null
        // данные только что загружены — это не правки пользователя
        this.$nextTick(() => { this.loadingSchema = false; this.dirty = false })
      })
    },
    // Перезагрузить ТОЛЬКО исходы, не трогая этапы и выбранный этап — чтобы панель
    // не закрывалась и не сбивались несохранённые правки этапа при работе с исходами.
    reloadOutcomes() {
      if (!this.selectedProcessId) return
      this.api('bpm_schema', { process_def_id: this.selectedProcessId }).then(r => {
        if (!r) return
        this.outcomes = (r.outcomes || []).map(o => ({ ...o, next_stage_id: o.next_stage_id || null, descr: o.descr || "", slotMap: this.parseMap(o.slot_map_json) }))
      })
    },
    newProcess() {
      // eslint-disable-next-line no-alert
      const name = window.prompt('Название нового процесса:')
      if (!name) return
      this.api('bpm_create_process', { name }).then(r => {
        if (!r) return
        this.loadProcesses()
        this.selectedProcessId = r.id
        this.loadSchema()
      })
    },
    addStage() {
      // новый этап ставим со смещением по сетке, чтобы он не ложился ПОВЕРХ старого
      const k = this.nodes.length
      const px = 40 + (k % 4) * 180
      const py = 40 + Math.floor(k / 4) * 110
      this.api('bpm_save_stage', {
        process_def_id: this.selectedProcessId, label: 'Новый этап', pos_x: px, pos_y: py,
      }).then(r => {
        if (!r) return
        this.quiet(() => {
          this.nodes.push({
            id: r.id, label: 'Новый этап', assignees_expr: '', descr: '', deadline_minutes: 0, instructions: '',
            form_obj_id: 0, executor_kind: 'HUMAN', subprocess_def_id: null, in_slot_map_json: '', out_slot_map_json: '', action_kind: 'NONE', action_object_id: null, actionMap: [], x: px, y: py,
          })
          this.selectedStageId = r.id
        })
      })
    },
    selectStage(id) {
      this.selectedStageId = id
    },
    // ---- право на запуск процесса ----
    onPickStartableRole(sid) {
      if (sid) {
        const cur = (this.startableBy || '').trim()
        const parts = cur ? cur.split(/\s*(?:,|\bor\b)\s*/i).map(s => s.trim()).filter(Boolean) : []
        const tok = `role('${sid}')`
        if (!parts.includes(tok)) parts.push(tok)
        this.startableBy = parts.join(', ')
      }
      this.startablePick = null
    },
    saveStartable() {
      this.api('bpm_set_startable', { process_def_id: this.selectedProcessId, expr: this.startableBy || '' })
        .then(r => { if (r) useCubus.toastMessage(this, 'Право на запуск сохранено') })
    },
    // ---- запуск экземпляра процесса ----
    openLaunch() {
      if (!this.process || this.process.status_code !== 'published' || !this.entryStageId) return
      const entry = this.nodes.find(n => n.id === this.entryStageId)
      const sess = this.$store.getters['cubus-store/SESSION']
      this.launch.startedId = null
      this.launch.loading = false
      this.launch.entryLabel = entry ? entry.label : '—'
      this.launch.initiatorName = (sess && sess.user && (sess.user.name || sess.user.username)) || 'вы'
      this.launch.preview = { users: [], notes: [] }
      this.launch.show = true
      // показываем, КОМУ уйдёт первая задача (резолв исполнителей стартового этапа)
      this.api('bpm_assignees_preview', { expr: entry ? (entry.assignees_expr || '') : '' }).then(r => {
        if (r) this.launch.preview = r.preview || { users: [], notes: [] }
      })
    },
    confirmLaunch() {
      this.launch.loading = true
      this.api('bpm_start', { process_def_id: this.selectedProcessId }).then(r => {
        this.launch.loading = false
        if (r && r.instance_id) {
          this.launch.startedId = r.instance_id
          useCubus.toastMessage(this, `Экземпляр #${r.instance_id} запущен`)
        }
      })
    },
    goAfterLaunch(routeName) {
      this.launch.show = false
      this.$router.push({ name: routeName }).catch(() => {})
    },
    // локально, без перезагрузки: пометить как загрузку, чтобы не сбить dirty
    quiet(fn) { this.loadingSchema = true; fn(); this.$nextTick(() => { this.loadingSchema = false }) },
    removeStage(n) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Удалить этап «${n.label}»?`)) return
      this.api('bpm_delete_stage', { id: n.id }).then(r => {
        if (!r) return
        this.quiet(() => {
          this.selectedStageId = null
          this.nodes = this.nodes.filter(x => x.id !== n.id)
          this.outcomes = this.outcomes.filter(o => o.stage_id !== n.id && o.next_stage_id !== n.id)
          this.outparams = this.outparams.filter(op => op.stage_id !== n.id)
          this.inparams = this.inparams.filter(p => p.stage_id !== n.id)
          if (this.entryStageId === n.id) this.entryStageId = null
        })
      })
    },
    stageOutcomes(stageId) {
      return this.outcomes.filter(o => o.stage_id === stageId)
    },
    parseMap(json) {
      if (!json) return []
      try { const a = JSON.parse(json); return Array.isArray(a) ? a.filter(m => m && m.from).map(m => ({ from: m.from, to: m.to || m.from })) : [] } catch (e) { return [] }
    },
    addMap(o) {
      if (!o.slotMap) this.$set(o, 'slotMap', [])
      const first = this.stageOutparams(o.stage_id)[0]
      o.slotMap.push({ from: first ? first.param_name : '', to: first ? first.param_name : '' })
    },
    parseActionMap(json) {
      if (!json) return []
      try { const a = JSON.parse(json); return Array.isArray(a) ? a.map(m => ({ to: m.to || '', src: m.src || (m.from ? 'slot' : 'const'), value: m.value != null ? m.value : (m.from || '') })) : [] } catch (e) { return [] }
    },
    addActionMap() {
      if (!this.selectedStage.actionMap) this.$set(this.selectedStage, 'actionMap', [])
      const first = this.stageOutparams(this.selectedStage.id)[0]
      this.selectedStage.actionMap.push({ to: '', src: 'slot', value: first ? first.param_name : '' })
    },
    notifyText() {
      const m = (this.selectedStage.actionMap || []).find(x => x.to === 'message')
      return m ? m.value : ''
    },
    setNotifyText(v) {
      if (!this.selectedStage.actionMap) this.$set(this.selectedStage, 'actionMap', [])
      let m = this.selectedStage.actionMap.find(x => x.to === 'message')
      if (!m) { m = { to: 'message', src: 'const', value: '' }; this.selectedStage.actionMap.push(m) }
      m.src = 'const'; m.value = v
      this.markDirty()
    },
    notifyRecipient() {
      const m = (this.selectedStage.actionMap || []).find(x => x.to === 'recipient')
      return m ? m.value : '$initiator'
    },
    setNotifyRecipient(v) {
      if (!this.selectedStage.actionMap) this.$set(this.selectedStage, 'actionMap', [])
      let m = this.selectedStage.actionMap.find(x => x.to === 'recipient')
      if (!m) { m = { to: 'recipient', src: 'const', value: '' }; this.selectedStage.actionMap.push(m) }
      m.src = 'const'; m.value = v
      this.markDirty()
    },
    outputOptions(stageId) {
      const opts = this.stageOutparams(stageId).map(op => ({ value: op.param_name, text: op.param_name }))
      return opts.length ? opts : [{ value: '', text: '— нет выходов у этапа —' }]
    },
    nextStageOptions(stageId) {
      return [{ value: null, text: '→ Завершить процесс' }]
        .concat(this.nodes.filter(n => n.id !== stageId).map(n => ({ value: n.id, text: `→ ${n.label}` })))
    },
    addOutcome(stage) {
      this.api('bpm_save_outcome', { stage_id: stage.id, label: 'исход', outcome_type: 'manual' })
        .then(r => { if (r) this.quiet(() => this.outcomes.push({ id: r.id, stage_id: stage.id, label: 'исход', outcome_type: 'manual', next_stage_id: null, descr: '', slotMap: [] })) })
    },
    removeOutcome(o) {
      this.api('bpm_delete_outcome', { id: o.id }).then(r => { if (r) this.quiet(() => { this.outcomes = this.outcomes.filter(x => x.id !== o.id) }) })
    },
    stageOutparams(stageId) {
      return this.outparams.filter(op => op.stage_id === stageId)
    },
    executorKindLabel(kind) {
      return { HUMAN: 'человек', AI: 'ИИ', AUTO: 'авто', SUBPROCESS: 'подпроцесс' }[kind || 'HUMAN'] || 'человек'
    },
    addOutparam(stage) {
      this.api('bpm_save_output', { stage_id: stage.id, param_name: 'результат', required: false, datatype: 'string' })
        .then(r => { if (r) this.quiet(() => this.outparams.push({ id: r.id, stage_id: stage.id, param_name: 'результат', required: false, datatype: 'string', ref_object_id: null })) })
    },
    deleteOutparam(op) {
      this.api('bpm_delete_output', { id: op.id }).then(r => { if (r) this.quiet(() => { this.outparams = this.outparams.filter(x => x.id !== op.id) }) })
    },
    stageInparams(stageId) {
      return this.inparams.filter(p => p.stage_id === stageId)
    },
    addInparam(stage) {
      this.api('bpm_save_input', { stage_id: stage.id, param_name: 'данные', required: false, datatype: 'string' })
        .then(r => { if (r) this.quiet(() => this.inparams.push({ id: r.id, stage_id: stage.id, param_name: 'данные', required: false, datatype: 'string', ref_object_id: null })) })
    },
    deleteInparam(p) {
      this.api('bpm_delete_input', { id: p.id }).then(r => { if (r) this.quiet(() => { this.inparams = this.inparams.filter(x => x.id !== p.id) }) })
    },
    publish() {
      if (!this.entryStageId) { useCubus.toastError(this, 'Выберите стартовый этап'); return }
      // сначала сохраняем все правки (публикация перезагружает схему — иначе несохранённое потеряется)
      const doPublish = () => this.api('bpm_publish', { process_def_id: this.selectedProcessId, entry_stage_id: this.entryStageId })
        .then(r => { if (r) { useCubus.toastMessage(this, 'Процесс опубликован'); this.loadProcesses(); this.loadSchema() } })
      if (this.dirty) this.saveAll().then(ok => { if (ok) doPublish() })
      else doPublish()
    },
    startDrag(n, ev) {
      this.selectedStageId = n.id
      this.drag = {
        node: n, startX: ev.clientX, startY: ev.clientY, originX: n.x, originY: n.y, moved: false,
      }
    },
    onDrag(ev) {
      if (!this.drag) return
      const dx = ev.clientX - this.drag.startX
      const dy = ev.clientY - this.drag.startY
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.drag.moved = true
      this.drag.node.x = Math.max(0, this.drag.originX + dx)
      this.drag.node.y = Math.max(0, this.drag.originY + dy)
    },
    endDrag() {
      if (!this.drag) return
      const { node, moved } = this.drag
      this.drag = null
      if (moved) {
        this.api('bpm_move_stage', { id: node.id, pos_x: Math.round(node.x), pos_y: Math.round(node.y) })
      }
    },
  },
}
</script>

<style scoped>
.bpm-canvas {
  position: relative;
  height: 560px;
  overflow: auto;
  background-color: #fafafc;
  background-image:
    linear-gradient(#eef0f4 1px, transparent 1px),
    linear-gradient(90deg, #eef0f4 1px, transparent 1px);
  background-size: 20px 20px;
  border: 1px solid #e4e6eb;
  border-radius: 6px;
}
.bpm-edges {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.bpm-stage {
  position: absolute;
  width: 150px;
  min-height: 54px;
  padding: 6px 8px;
  background: #fff;
  border: 1px solid #d8dbe0;
  cursor: move;
  user-select: none;
}
.bpm-stage.border-primary {
  border-width: 2px;
}
.bpm-entry {
  border-left: 3px solid #28c76f;
}
.bpm-props {
  width: 320px;
  flex: 0 0 320px;
  max-height: 560px;
  overflow: auto;
}
.bpm-stagelist {
  width: 190px;
  flex: 0 0 190px;
  max-height: 560px;
  overflow: auto;
  background: #fafafc;
}
.bpm-stage-item {
  border: 1px solid #e4e6eb;
  background: #fff;
  transition: background-color .12s, border-color .12s;
}
.bpm-stage-item:hover {
  border-color: #b8bcc4;
}
.bpm-stage-item-active {
  border-color: #7367f0;
  background: #f3f2ff;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
